<script setup lang="ts">
import {RcsPayload, RcsSuggestion} from "@/notification-template/interfaces";
import {ref} from "vue";
import RCSText from "@/notification-template/components/rcs/RCSText.vue";
import RCSCard from "@/notification-template/components/rcs/RCSCard.vue";
import RCSCarousel from "@/notification-template/components/rcs/RCSCarousel.vue";

defineProps<{
    payload: RcsPayload
}>();

const webviewUrl = ref<string | null>(null);
const iframeBlocked = ref(false);

/**
 * Handle suggestion click.
 */
const handleSuggestion = (suggestion: RcsSuggestion) => {
    if (suggestion.type === 'url') {
        window.open(suggestion.url, '_blank');
    } else if (suggestion.type === 'webview') {
        iframeBlocked.value = false;
        webviewUrl.value = suggestion.url;
    }
};

const onIframeError = () => {
    iframeBlocked.value = true;
};
</script>

<template>
  <div class="max-w-sm relative rcs-view">
    <img
      src="@/assets/images/iphone-mockup-transparent.png" class="w-full object-contain pointer-events-none"
      alt="mockup"
    >
    <div
      class="select-none absolute h-[91.7%] w-[84%] left-[calc(50%)] -translate-x-1/2 top-[2.8%] rounded-[2rem] from-10% via-20% to-30% overflow-hidden bg-gradient-to-b from-gray-200 to-white flex flex-col"
    >
      <div class="self-center mt-4 space-y-1">
        <div class="w-12 h-12 rounded-md shadow-lg p-3 mx-auto bg-white">
          <img src="@/assets/images/customer-app-logo.png" class="w-full object-contain h-full" alt="Apodis">
        </div>
        <div class="flex-1">
          <div class="font-medium text-sm">
            Apodis
          </div>
        </div>
      </div>

      <div class="flex-1 p-4 overflow-y-auto">
        <div class="flex flex-col gap-1">
          <RCSText
            v-if="payload?.type === 'text'" class="max-w-[85%]" :payload="payload"
            @suggestion-click="handleSuggestion"
          />
          <RCSCard
            v-if="payload?.type === 'card'" :payload="payload"
            @suggestion-click="handleSuggestion"
          />
          <RCSCarousel
            v-if="payload?.type === 'carousel'" :payload="payload"
            @suggestion-click="handleSuggestion"
          />
        </div>
      </div>

      <Transition name="slide-up">
        <div v-if="webviewUrl" class="absolute inset-0 bg-white flex flex-col rounded-[2rem] overflow-hidden">
          <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
            <button class="text-primary text-xs font-medium" @click="webviewUrl = null">
              Fermer
            </button>
            <a :href="webviewUrl" target="_blank" class="text-primary text-xs font-medium">
              Ouvrir dans le navigateur
            </a>
          </div>
          <div v-if="iframeBlocked" class="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <p class="text-sm text-gray-500">
              Ce site ne peut pas être affiché ici.
            </p>
            <a :href="webviewUrl" target="_blank" class="text-primary text-xs font-medium underline">
              Ouvrir dans le navigateur
            </a>
          </div>
          <iframe v-else :src="webviewUrl" class="flex-1 w-full border-none" @error="onIframeError" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
    transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(100%);
}
</style>
